import { Head, Link } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Vendor {
  id: number
  display_name: string
}

interface ScheduleEntry {
  id: number
  case_id: number
  case_name: string | null
  day_name: string | null
  date_desc: string | null
  time_desc: string | null
  title: string | null
  notes: string | null
  team: string | null
  vendors: string | null
}

interface Props {
  vendor: Vendor
  schedules: ScheduleEntry[]
}

export default function Index({ vendor, schedules }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Subcontractors", href: "/company/vendors" },
    { title: vendor.display_name, href: `/company/vendors/${vendor.id}` },
    { title: "Schedule", href: `/company/vendors/${vendor.id}/schedules` },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${vendor.display_name} - Schedule`} />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">
          Scheduled Work for {vendor.display_name}
        </h1>

        {schedules.length > 0 ? (
          <div className="border-hpi-light-gray rounded border">
            <div className="bg-hpi-pale-gray border-hpi-light-gray grid grid-cols-[1fr_5fr] border-b px-4 py-2 font-semibold">
              <div>Date</div>
              <div>Description</div>
            </div>
            {schedules.map((entry) => (
              <Link
                key={entry.id}
                href={`/cases/${entry.case_id}/schedules/${entry.id}`}
                className="border-hpi-light-gray hover:bg-hpi-pale-blue grid grid-cols-[1fr_5fr] border-b px-4 py-3 text-inherit no-underline last:border-b-0"
              >
                <div>
                  {entry.day_name && (
                    <div className="text-hpi-med-gray text-sm">
                      {entry.day_name}
                    </div>
                  )}
                  <div>{entry.date_desc}</div>
                  {entry.time_desc && (
                    <div className="text-hpi-med-gray text-sm">
                      {entry.time_desc}
                    </div>
                  )}
                </div>
                <div>
                  {entry.title ? (
                    <>
                      <div>{entry.title}</div>
                      {entry.notes && (
                        <div className="text-hpi-med-gray text-sm whitespace-pre-wrap">
                          {entry.notes}
                        </div>
                      )}
                    </>
                  ) : (
                    <div>{entry.notes}</div>
                  )}
                  {entry.team && (
                    <div className="text-hpi-med-gray mt-2 text-sm">
                      {entry.team}
                    </div>
                  )}
                  {entry.vendors && (
                    <div className="text-hpi-med-gray mt-2 text-sm">
                      Subcontractors: {entry.vendors}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="bg-hpi-pale-blue/50 border-hpi-light-gray rounded border p-4 text-sm">
            No scheduled work found.
          </div>
        )}
      </div>
    </AppLayout>
  )
}
